import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  type TaskPriority,
  type TaskStatus,
} from '@lawyer/shared'

const pad = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM-DD` (a calendar day) rendered as `30/08/2023`, without any timezone shift. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return '—'
  return `${pad(day)}/${pad(month)}/${year}`
}

/** Full timestamp as `30/08/2023 14:05`, in the viewer's local time. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Today's calendar day as `YYYY-MM-DD`, which is what the API expects. */
export function todayInputValue(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function addDaysInputValue(days: number): string {
  const now = new Date()
  now.setDate(now.getDate() + days)
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return null
  const target = Date.UTC(year, month - 1, day)
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((target - today) / 86_400_000)
}

export function dueDateHint(value: string | null | undefined): string {
  const diff = daysUntil(value)
  if (diff === null) return ''
  if (diff < -1) return `באיחור של ${Math.abs(diff)} ימים`
  if (diff === -1) return 'באיחור מאתמול'
  if (diff === 0) return 'להיום'
  if (diff === 1) return 'למחר'
  if (diff <= 7) return `בעוד ${diff} ימים`
  return ''
}

export const statusLabel = (status: TaskStatus): string => TASK_STATUS_LABELS[status]
export const priorityLabel = (priority: TaskPriority): string => TASK_PRIORITY_LABELS[priority]

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

export const statusColor = (status: TaskStatus): ChipColor => {
  switch (status) {
    case 'TODO':
      return 'default'
    case 'IN_PROGRESS':
      return 'primary'
    case 'WAITING_CLIENT':
      return 'warning'
    case 'WAITING_EXTERNAL':
      return 'secondary'
    case 'DONE':
      return 'success'
    case 'CANCELLED':
      return 'default'
  }
}

export const priorityColor = (priority: TaskPriority): ChipColor => {
  switch (priority) {
    case 'LOW':
      return 'default'
    case 'MEDIUM':
      return 'info'
    case 'HIGH':
      return 'warning'
    case 'URGENT':
      return 'error'
  }
}

const moneyFormatter = new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return moneyFormatter.format(value)
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} ב׳`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} ק״ב`
  return `${(bytes / (1024 * 1024)).toFixed(1)} מ״ב`
}
